import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { useAuthStore } from "@/stores/auth-store";
import RefreshService from "@/api/refresh-service";
import { AUTH_ROUTES } from "@/utils/navigation";
import i18next from "@/translations/config";
import brandConfig from "@/brand/config.json";

// Extend AxiosRequestConfig to include retry property
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface ResponseWithHeaders<T> {
  data: T;
  headers: Record<string, string>;
}

// Endpoints that carry no bearer token: renewing here would let a stale session
// left in localStorage abort the very request meant to replace it. `/logout` is
// absent because that one does need the token.
const UNAUTHENTICATED_PATHS = ["/staff/login", "/password/"];

/**
 * APIClient with proactive token refresh
 */
class APIClient {
  private static instance: APIClient;
  private client: AxiosInstance;

  private constructor(headers: Record<string, string> = {}) {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        ...headers,
        // Tells the API which brand this build is, so it picks the matching
        // per-brand host for reset/confirmation email links. Kept last so it
        // can't be overridden by caller-provided headers.
        "X-Organization": brandConfig.brand.slug,
      },
    });

    this.setupInterceptors();
  }

  public static getInstance(headers?: Record<string, string>): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient(headers);
    }
    return APIClient.instance;
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      async (config) => {
        // RefreshService is the single place deciding whether a renewal is due.
        if (this.needsAuth(config.url)) {
          try {
            await RefreshService.getInstance().refreshTokenIfNeeded();
          } catch (error) {
            // Only the definitive rejection reaches here, session already cleared.
            this.redirectToSignIn();
            throw error;
          }
        }

        // Read the token after the refresh so a renewed one is the one sent.
        const token = useAuthStore.getState().token();
        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
        }
        config.headers.set("Accept-Language", i18next.language);
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          useAuthStore.getState().clearSession();
          this.redirectToSignIn();

          return Promise.reject(error);
        }

        return Promise.reject(error);
      },
    );
  }

  private needsAuth(url?: string) {
    return !UNAUTHENTICATED_PATHS.some((path) => url?.startsWith(path));
  }

  private redirectToSignIn() {
    const isOnAuthRoute = Object.values(AUTH_ROUTES).some((route) =>
      window.location.pathname.startsWith(route),
    );

    if (!isOnAuthRoute) {
      window.location.href = AUTH_ROUTES.SIGN_IN;
    }
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  public async getWithHeaders<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ResponseWithHeaders<T>> {
    const response = await this.client.get(url, config);
    return {
      data: response.data,
      headers: response.headers as Record<string, string>,
    };
  }

  public async post<T>(
    url: string,
    data: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  public async put<T>(
    url: string,
    data: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  public async patch<T>(
    url: string,
    data: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data;
  }
}

export default APIClient;
